import React, { useCallback, useEffect, useState } from 'react'
// @ts-ignore
import styled from 'styled-components'
// @ts-ignore
import type { RouteComponentProps } from 'react-router-dom'
import TypographyPro from 'themes/TypographyPro'
import { getTenantInfo, getTranslatedTextByKey } from 'utils/utils'
import IconComponent from 'themes/IconComponent'
import { Grid, IconButton, Radio } from '@material-ui/core'
import { inject, observer } from 'mobx-react'
import OnlinePaymentIcon from 'components/common/OnlinePaymentIcon'
import apiPayments from 'utils/api/apiPayments'
import { breakpoints } from 'styles/mixins'
import type { Card, Cards } from './AccountSavedCards.type'
import { SnackbarStatus } from 'mobx/Infra/Infra.type'

const TrashCan = (props: {}) => <IconComponent asset="/icons/trashCan.svg" {...props} />

const pageTitle = getTranslatedTextByKey('eCommerce.accountSettings.savedPaymentMethods.title', 'Saved payment methods')
const defaultSavedCardTitle = getTranslatedTextByKey('eCommerce.accountSettings.savedPaymentMethods.defaultCard', 'Default card')
const savedCardsTitle = getTranslatedTextByKey('eCommerce.accountSettings.savedPaymentMethods.chooseDefault', 'Choose to make default')
const cardSavedSuccessfully = (lastFour?: string) =>
	getTranslatedTextByKey(
		'eCommerce.accountSettings.savedPaymentMethods.cardSavedSuccessfully',
		`Your card **** ${lastFour} has been saved as default successfully!`
	)

const userHaveNoCardsMessage = getTranslatedTextByKey(
	'eCommerce.accountSettings.savedPaymentMethods.userHaveNoCards',
	`You don’t have any payment methods in your account yet!`
)

const dialogTitle = getTranslatedTextByKey('webviewFlow.deletePaymentMethod', 'Delete payment method')
const dialogMessage = getTranslatedTextByKey('webviewFlow.deletePaymentConfirmation', 'Are you sure you want to delete this payment method?')
const dialogOkButton = getTranslatedTextByKey('webviewFlow.delete', 'Delete')
const dialogCancelButton = getTranslatedTextByKey('webviewFlow.cancel', 'Cancel')

interface ThemeProps {
	theme?: {
		typography: {
			BodySmallHighlighted: string
			h2: string
			BodySmall: string
		}
	}
}

interface OneCardProps {
	isChecked: boolean
	noCards?: boolean
}

const OneCardWrapper = styled.div`
	background-color: ${(props: OneCardProps) => (props.isChecked ? 'var(--successBg)' : 'var(--clear)')};
	box-shadow: ${(props: OneCardProps) => !props.isChecked && 'var(--defaultShadow1)'};
	border-radius: var(--borderRadiusInput);
	border: ${(props: OneCardProps) => props.isChecked && '1px solid var(--approved)'};
	padding: 6px 20px;
	margin-block-end: 10px;
	display: flex;
	align-items: center;
	cursor: pointer;
	position: relative;
	height: 70px;

	@media (max-width: 599px) {
		padding: 6px 10px;
		height: 93px;
	}
`

const RadioStyle = styled(Radio)`
	width: 34px;
	height: 34px;
	color: var(--inactive);
	margin-right: 10px;
	&.Mui-checked {
		color: var(--approved);
	}
	.MuiSvgIcon-root:last-child {
		width: 34px;
		height: 34px;
		position: absolute;
		left: -5px;
		top: -5px;
	}
`

const CardTitle = styled(TypographyPro)<ThemeProps>`
	color: var(--fonts);
	${(props: ThemeProps) => props?.theme?.typography.BodySmallHighlighted};
	padding-inline-start: 19px;
`

const TrashCanStyle = styled(TrashCan)`
	width: 16px;
	height: 20px;
`

// todo: too smart, we must break it
const CardIcon = styled(OnlinePaymentIcon)`
	height: ${(props: OneCardProps) => (props.noCards ? '150px' : '55px')};
	width: ${(props: OneCardProps) => (props.noCards ? '150px' : '55px')};

	path {
		fill: ${(props: OneCardProps) => (props.isChecked && !props.noCards ? 'var(--fonts)' : 'var(--disable)')};
	}

	@media (max-width: 599px) {
		height: ${(props: OneCardProps) => (props.noCards ? '100px' : '40px')};
		width: ${(props: OneCardProps) => (props.noCards ? '100px' : '50px')};
	}
`

const AccountPaymentsMethodsWrapper = styled.div`
	display: flex;
	flex-direction: column;
	max-width: 640px;
	width: 100%;
	padding-block-end: 100px;

	@media (max-width: 599px) {
		max-width: 100%;
		padding: 20px;
	}
`

const Title = styled(TypographyPro)`
	text-transform: uppercase;
	color: var(--strokeMenuTitle);
`

const DefaultSavedCard = styled.div`
	margin-block-start: 50px;
`

const SavedCards = styled.div`
	margin-block-start: 25px;
`

const SubTitle = styled(TypographyPro)`
	color: var(--disable);
	padding-block-end: 10px;
`

const IconButtonStyle = styled(IconButton)`
	color: var(--disable);
	${(props: ThemeProps) => props?.theme?.typography.BodySmall};
	z-index: 1;
`

const NoCardsWrapper = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	padding-block-start: 50px;
	@media (max-width: ${breakpoints.xs}) {
		padding-block-start: 30px;
	}
`

const NoCardsText = styled.div`
	color: var(--inactive);
	width: 100%;
	@media (max-width: ${breakpoints.xs}) {
		text-align: center;
	}
`

export const getDefaultCardObj = (cards: Cards, isDefaultCard = false): Card | {} => {
	if (cards?.length) {
		if (cards?.length === 1 || isDefaultCard) {
			return cards[0]
		}
		return cards.find((item: Card) => item.default) as Card
	}
	return {}
}

export const AccountSavedCards = inject(
	'Infra',
	'Account',
	'Application'
)(
	observer(({ Infra, Account, Application }: { Infra: any; Account: any; Application: any } & RouteComponentProps) => {
		/** this state will control which card user choose as a default and the changes will display
		 *  we do that because we cant see the changes from server immediately so the state will control it
		 */
		const [loaded, setLoaded] = useState(false)
		const [defaultCard, setDefaultCard] = useState<Card>()
		const [cards, setCards] = useState<Cards | undefined>()

		useEffect(() => {
			;(async () => {
				try {
					const tenantId = getTenantInfo()
					const cardsList: Cards = await apiPayments.getSavedCreditCards(tenantId, Account, Application.backendChannel)
					if (cardsList && cardsList.length > 0) {
						setCards(cardsList)
						const defaultCard: Card = getDefaultCardObj(cardsList)
						setDefaultCard(defaultCard)
					}
					setLoaded(true)
				} catch (err) {
					console.error(err)
				}
			})()
		}, [])

		const deleteCard = async (cardId?: string) => {
			try {
				await apiPayments.deleteCreditCard(cardId ?? '', Account, Application.backendChannel)
				const filteredCards: Cards = (cards ?? []).filter((item: Card) => item.id !== cardId)
				setCards(filteredCards)
				const isDefaultCardDeleted = cardId === defaultCard?.id
				if (isDefaultCardDeleted) {
					setDefaultCard(getDefaultCardObj(filteredCards, true))
				}
			} catch (err) {
				console.error(err)
			}
		}

		const setCardAsDefault = useCallback(async (card: Card) => {
			try {
				const tenantId = getTenantInfo()
				await apiPayments.setCreditCardAsDefault(tenantId, card?.id ?? '', Account, Application.backendChannel)
				setDefaultCard(card)
			} catch (err) {
				console.error(err)
			}
		}, [])

		const handleChange = useCallback(async (item: Card) => {
			await setCardAsDefault(item)
			Infra.showSnackbar({
				isAttachedToElement: false,
				message: cardSavedSuccessfully(item.lastFour),
				status: SnackbarStatus.SUCCESS,
			})
		}, [])

		const OneCard = (item: Card) => {
			const itemId = item?.id
			const lastFour = item?.lastFour
			const isChecked = itemId === defaultCard?.id
			return (
				<OneCardWrapper key={itemId} isChecked={isChecked}>
					<Grid item xs={11} container alignItems="center" onClick={() => !isChecked && handleChange({ ...item })}>
						<RadioStyle checked={isChecked} onChange={() => !isChecked && handleChange({ ...item })} value={itemId} />
						<Grid item xs={1} container alignItems="center">
							<CardIcon isChecked={isChecked} />
						</Grid>
						<Grid item xs>
							<CardTitle component="div" variant="BodySmallHighlighted">
								**** {lastFour}
							</CardTitle>
						</Grid>
					</Grid>
					<Grid item xs={3} sm={1} container alignItems="center" justifyContent="flex-end">
						<IconButtonStyle aria-label="delete saved card" onClick={() => deleteConfirmationDialog(itemId)}>
							<TrashCanStyle />
						</IconButtonStyle>
					</Grid>
				</OneCardWrapper>
			)
		}

		const deleteConfirmationDialog = (itemId?: string) => {
			Infra.setNotification({
				open: true,
				closable: false,
				title: dialogTitle,
				message: dialogMessage,
				cancelText: dialogCancelButton,
				okText: dialogOkButton,
				closeAction: true,
				okAction: () => {
					deleteCard(itemId)
						.catch((err) => console.error(err))
						.finally(() => {
							Infra.closeNotification()
						})
				},
				cancelAction: () => {
					Infra.closeNotification()
				},
			})
		}

		if (!loaded) {
			return null
		}

		const displayCards = !!cards && !!cards.length

		return (
			<>
				{!displayCards && (
					<div>
						<Title variant="h2">{pageTitle}</Title>
						<AccountPaymentsMethodsWrapper>
							<NoCardsWrapper>
								<CardIcon noCards />
								<NoCardsText>
									<TypographyPro variant="BodyRegular">{userHaveNoCardsMessage}</TypographyPro>
								</NoCardsText>
							</NoCardsWrapper>
						</AccountPaymentsMethodsWrapper>
					</div>
				)}

				{displayCards && (
					<AccountPaymentsMethodsWrapper>
						<Title variant="h2">{pageTitle}</Title>
						<DefaultSavedCard>
							{cards?.length > 1 && (
								<SubTitle component="div" variant="BodySmall">
									{defaultSavedCardTitle}
								</SubTitle>
							)}
							{defaultCard && <OneCard {...defaultCard} />}
						</DefaultSavedCard>
						{cards?.length > 1 && (
							<SavedCards>
								<SubTitle component="div" variant="BodySmall">
									{savedCardsTitle}
								</SubTitle>
								{cards?.map((item: Card) => item?.id !== defaultCard?.id && <OneCard key={item.id} {...item} />)}
							</SavedCards>
						)}
					</AccountPaymentsMethodsWrapper>
				)}
			</>
		)
	})
)
